/**
 * The pure part of the "my signatures and sender" window: how one of a
 * person's own signatures ends a letter, and the rows the window starts from.
 *
 * `ownPreview` is a twin of the API's `ownParts` (`crm-email/lib/signoff.ts`):
 * the window draws the result while the fields are still being typed in, so it
 * cannot ask the server each time. The tests here and there pin the same cases.
 */
import type { MailCardPreview } from "../../../../lib/mail";
import type { OwnSignature } from "../../../../lib/mailSignature";

/** What a signature adds under a letter: grey text, then a card. Either may be empty. */
export interface PreviewParts {
  text: string;
  card: MailCardPreview | null;
}

/** The plain lines: the closing, a gap, the name and its lines. */
export function ownText(sig: OwnSignature): string {
  const who = [sig.name.trim(), lines(sig.details)].filter(Boolean).join("\n");
  return [sig.closing.trim(), who].filter(Boolean).join("\n\n");
}

/** The lines under the name, as the server keeps them: trimmed, no blank ones. */
function lines(details: string): string {
  return details
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
}

/**
 * Card off, or no company card to add: the plain lines. Card on: ONE card, the
 * company's mark and bar beside the person's name and lines, then the
 * company's name and contacts (less any the person already wrote), with the
 * closing above it.
 */
export function ownPreview(sig: OwnSignature, company: MailCardPreview | null): PreviewParts {
  if (!sig.card || !company) return { text: ownText(sig), card: null };
  const name = sig.name.trim();
  const mine = [name, ...lines(sig.details).split("\n")].filter(Boolean);
  const theirs = [name ? company.name : "", ...company.details.split("\n")]
    .filter(Boolean)
    .filter((line) => !repeatsLine(line, mine));
  const details = [lines(sig.details), ...theirs].filter(Boolean).join("\n");
  return {
    text: "",
    card: { closing: sig.closing.trim(), name: name || company.name, details, accent: company.accent, logoUrl: company.logoUrl },
  };
}

/** The digits of a line that reads as a phone number (six or more), else "". */
function phoneDigits(line: string): string {
  const digits = line.replace(/\D/g, "");
  return digits.length >= 6 ? digits : "";
}

const EMAIL_IN_LINE = /[^\s@]+@[^\s@]+\.[^\s@]+/;

/**
 * Whether a company line only says again what the person already wrote: the
 * same line, the same email, or the same phone number with or without its
 * country code. Twin of the API's `repeatsLine`.
 */
export function repeatsLine(line: string, mine: string[]): boolean {
  const flat = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
  if (mine.some((m) => flat(m) === flat(line))) return true;
  const email = line.match(EMAIL_IN_LINE)?.[0]?.toLowerCase();
  if (email && mine.some((m) => m.toLowerCase().includes(email))) return true;
  const phone = phoneDigits(line);
  if (!phone) return false;
  return mine.some((m) => {
    const other = phoneDigits(m);
    return other !== "" && (phone.endsWith(other) || other.endsWith(phone));
  });
}

/** Ids only have to be unique within one person's list. `[A-Za-z0-9_-]`, as the API checks. */
export function newSignatureId(): string {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * The first signature a person is offered, filled in so one press of Save is
 * enough: their name from the account, the usual closing, and the company card
 * when there is one to add.
 */
export function starterSignature(input: { label: string; closing: string; name: string; card: boolean }): OwnSignature {
  return { id: newSignatureId(), label: input.label, closing: input.closing, name: input.name.trim(), details: "", card: input.card };
}

/** The default as the server settles it: a row's id, else the first row's. */
export function settleDefault(rows: OwnSignature[], id: string): string {
  return rows.some((r) => r.id === id) ? id : rows[0]?.id ?? "";
}
